import React, { useEffect, useState, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { getChargeStatistics } from '../../../subscriptions/core/subscriptionService';
import { useThemeMode } from '../../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type Props = {
  className?: string;
  chartColor?: string;
};

export const PaymentVolumeChart: React.FC<Props> = ({ className, chartColor = 'primary' }) => {
  const [series, setSeries] = useState<any[]>([]);
  const { mode } = useThemeMode();

  const isDark = useMemo(() => {
    if (mode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return mode === 'dark';
  }, [mode]);

  useEffect(() => {
    getChargeStatistics().then((data) => {
      if (data && data.volume_by_method) {
        const currentMonth = new Date().getMonth();

        const boletoData = Array(12).fill(0);
        const pixData = Array(12).fill(0);
        const cartaoData = Array(12).fill(0);

        data.volume_by_method.forEach((item: any) => {
          if (item.method === 'BOLETO') boletoData[currentMonth] = item.total;
          if (item.method === 'PIX') pixData[currentMonth] = item.total;
          if (item.method === 'CARTAO') cartaoData[currentMonth] = item.total;
        });

        setSeries([
          { name: 'Boleto', data: boletoData },
          { name: 'Pix', data: pixData },
          { name: 'Cartão', data: cartaoData },
        ]);
      }
    }).catch(() => {
      setSeries([
        { name: 'Boleto', data: Array(12).fill(0) },
        { name: 'Pix', data: Array(12).fill(0) },
        { name: 'Cartão', data: Array(12).fill(0) },
      ]);
    });
  }, []);

  const options: any = useMemo(() => {
    const labelColor = isDark ? '#cdcdde' : '#5e6278';
    const gridColor = isDark ? '#2b2b40' : '#eff2f5';

    return {
      chart: {
        type: 'bar' as const,
        height: 350,
        stacked: true,
        toolbar: { show: false },
        background: 'transparent',
      },
      theme: {
        mode: isDark ? 'dark' as const : 'light' as const,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 4,
          dataLabels: {
            total: {
              enabled: true,
              style: { fontSize: '13px', fontWeight: 900, color: labelColor },
            },
          },
        },
      },
      xaxis: {
        categories: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
        labels: {
          style: { colors: labelColor, fontSize: '12px' },
        },
        axisBorder: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: labelColor, fontSize: '12px' },
        },
      },
      grid: {
        borderColor: gridColor,
        strokeDashArray: 4,
      },
      legend: {
        position: 'top' as const,
        labels: {
          colors: labelColor,
        },
      },
      fill: { opacity: 1 },
      colors: ['#009EF7', '#50CD89', '#F1416C'],
    };
  }, [isDark]);

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>Volume por Método de Pagamento</span>
          <span className='text-muted fw-bold fs-7'>Receita total gerada pelas assinaturas</span>
        </h3>
      </div>
      <div className='card-body'>
        <Chart options={options} series={series} type='bar' height={350} />
      </div>
    </div>
  );
};
